import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as authLogin, logout as authLogout, isAuthenticated, getAuthUser, getAuthDomain, getAuthIsAdmin } from '../services/authApi';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [domain, setDomain] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const isAuth = isAuthenticated();
        const username = getAuthUser();
        
        if (isAuth && username) {
          setUser(username);
          setDomain(getAuthDomain() || username || 'innova.su');
          setIsAdmin(getAuthIsAdmin());
          setAuthenticated(true);
        } else {
          setUser(null);
          setDomain(null);
          setIsAdmin(false);
          setAuthenticated(false);
        }
      } catch (error) {
        console.error('[AuthContext] Error checking authentication:', error);
        setUser(null);
        setDomain(null);
        setIsAdmin(false);
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authLogin(username, password);
      setUser(response.username);
      setDomain(response.domain ?? response.username);
      setIsAdmin(response.isAdmin ?? false);
      setAuthenticated(true);
      return response;
    } catch (error) {
      setUser(null);
      setDomain(null);
      setIsAdmin(false);
      setAuthenticated(false);
      throw error;
    }
  };

  const logout = () => {
    authLogout();
    setUser(null);
    setDomain(null);
    setIsAdmin(false);
    setAuthenticated(false);
  };

  const value = {
    user,
    domain,
    isAdmin,
    isAuthenticated: authenticated,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
